import React, { useState, useMemo } from 'react';
import {
  DataTable,
  Button,
  Badge,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from 'lift-ui';
import type { TableColumn } from 'lift-ui';
import { Edit, Power, CheckCircle } from 'lucide-react';
import moment from 'moment';
import type { ReportConfiguration } from '../../types';
import { isBreakdownReport } from '../../utils/reportTypeUtils';
import { transformToUI } from '../../utils/valueTransformations';
import DeactivationDialog from '../DeactivationDialog';
import ActivationDialog from '../ActivationDialog';
import EditConfigDialog from '../EditConfigDialog';

interface ConfigurationTableProps {
  configurations: ReportConfiguration[];
  brandId: number;
  brandName?: string;
  hasFiscalCalendar: boolean;
  onRefresh: (showToast?: boolean) => void;
  refreshKey: number;
}

const ConfigurationTable: React.FC<ConfigurationTableProps> = ({
  configurations,
  brandId,
  brandName,
  hasFiscalCalendar,
  onRefresh,
  refreshKey,
}) => {
  const [selectedConfig, setSelectedConfig] = useState<ReportConfiguration | null>(null);
  const [isDeactivateOpen, setIsDeactivateOpen] = useState(false);
  const [isActivateOpen, setIsActivateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [currentTime, setCurrentTime] = useState(new Date());

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setRowsPerPage(newPageSize);
    setPage(0);
  };

  // Update current time whenever refresh is triggered
  React.useEffect(() => {
    setCurrentTime(new Date());
  }, [refreshKey]);

  const paginatedData = useMemo(() => {
    return configurations.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [configurations, page, rowsPerPage]);

  // Helper to compute display status
  const getDisplayStatus = (deactivationAt: string | null, reactivationAt: string | null) => {
    const now = new Date();
    
    // No deactivation scheduled
    if (!deactivationAt) {
      return {
        label: 'Active',
        color: 'green',
        type: 'active',
      };
    }

    const deactivationDate = new Date(deactivationAt);

    // Deactivation is in the future
    if (now < deactivationDate) {
      return {
        label: `Scheduled (${moment(deactivationDate).format('MM/DD/YYYY HH:mm')})`,
        color: 'orange',
        type: 'scheduled',
      };
    }

    // Deactivation has passed - check for reactivation
    if (reactivationAt) {
      const reactivationDate = new Date(reactivationAt);
      if (reactivationDate <= now) {
        // Reactivation date has passed
        return {
          label: 'Active',
          color: 'green',
          type: 'active',
        };
      }
      // Waiting for reactivation
      return {
        label: 'Deactivated',
        color: 'red',
        type: 'deactivated',
      };
    }

    // No reactivation scheduled
    return {
      label: 'Deactivated',
      color: 'red',
      type: 'deactivated',
    };
  };

  const getStatusBadge = (deactivationAt: string | null, reactivationAt: string | null) => {
    const status = getDisplayStatus(deactivationAt, reactivationAt);
    
    const colorClasses: Record<string, string> = {
      green: 'bg-green-100 text-green-800 border-green-300',
      orange: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      red: 'bg-red-100 text-red-800 border-red-300',
    };
    
    return (
      <Badge variant="outline" className={colorClasses[status.color]}>
        {status.label}
      </Badge>
    );
  };

  const columns: TableColumn<ReportConfiguration>[] = useMemo(
    () => [
      {
        key: 'platform',
        header: 'PLATFORM',
        accessor: 'platform',
        width: '15%',
        render: (_value, row) => (
          <span className="text-sm font-medium">{transformToUI(row.platform)}</span>
        ),
      },
      {
        key: 'report_type',
        header: 'REPORT TYPE',
        accessor: 'report_type',
        width: '15%',
        render: (_value, row) => (
          <span className="text-sm">{transformToUI(row.report_type)}</span>
        ),
      },
      {
        key: 'granularity_level',
        header: 'GRANULARITY',
        accessor: 'granularity_level',
        width: '12%',
        render: (_value, row) => (
          <span className="text-sm">{transformToUI(row.granularity_level)}</span>
        ),
      },
      {
        key: 'cadence',
        header: 'CADENCE',
        accessor: 'cadence',
        width: '12%',
        render: (_value, row) => (
          <span className="text-sm">{transformToUI(row.cadence)}</span>
        ),
      },
      {
        key: 'metric_type',
        header: 'METRIC TYPE',
        accessor: 'metric_type',
        width: '12%',
        render: (_value, row) => (
          <span className="text-sm">{transformToUI(row.metric_type)}</span>
        ),
      },
      {
        key: 'use_fiscal_calendar',
        header: 'FISCAL CAL.',
        accessor: 'use_fiscal_calendar',
        width: '10%',
        render: (_value, row) => {
          const displayValue = isBreakdownReport(row.report_type)
            ? hasFiscalCalendar
            : row.use_fiscal_calendar;
          return (
            <span className="text-sm">{displayValue ? 'Yes' : 'No'}</span>
          );
        },
      },
      {
        key: 'status',
        header: 'STATUS',
        accessor: 'status',
        width: '12%',
        render: (_value, row) => getStatusBadge(row.deactivation_at, row.reactivation_at),
      },
      {
        key: 'actions',
        header: 'ACTIONS',
        accessor: (row) => row.id,
        width: '12%',
        align: 'center',
        render: (_value, row) => {
          const status = getDisplayStatus(row.deactivation_at, row.reactivation_at);
          
          return (
            <div className="flex items-center justify-center gap-1">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedConfig(row);
                        setIsEditOpen(true);
                      }}
                      className="h-8 w-8 p-0"
                      disabled={status.type === 'deactivated'}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Edit</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {status.type === 'active' && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedConfig(row);
                          setIsDeactivateOpen(true);
                        }}
                        className="h-8 w-8 p-0"
                      >
                        <Power className="h-4 w-4 text-yellow-600" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Deactivate</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {(status.type === 'deactivated' || status.type === 'scheduled') && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedConfig(row);
                          setIsActivateOpen(true);
                        }}
                        className="h-8 w-8 p-0"
                      >
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Activate</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              
              {status.type === 'scheduled' && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedConfig(row);
                          setIsDeactivateOpen(true);
                        }}
                        className="h-8 w-8 p-0"
                      >
                        <Power className="h-4 w-4 text-orange-600" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Change Date</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          );
        },
      },
    ],
    [currentTime, hasFiscalCalendar]
  );

  const handleDialogClose = () => {
    setSelectedConfig(null);
    setIsDeactivateOpen(false);
    setIsActivateOpen(false);
    setIsEditOpen(false);
  };

  const handleSuccess = () => {
    handleDialogClose();
    onRefresh(false); // Don't show toast, the dialog already showed a success message
  };

  if (configurations.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 text-lg mb-2">No configurations found</p>
          <p className="text-gray-400 text-sm">
            Click "Add Configuration" to create your first report configuration
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex-1 min-h-0 flex flex-col">
        <DataTable<ReportConfiguration>
          className="flex-1 min-h-0 overflow-auto"
          maxHeight="calc(100vh - 280px)"
          columns={columns}
          data={paginatedData}
          emptyMessage="No configurations found."
          stickyHeader
          stickyFooter
          pagination={{
            currentPage: page + 1,
            totalPages: Math.max(1, Math.ceil(configurations.length / rowsPerPage)),
            pageSize: rowsPerPage,
            totalItems: configurations.length,
            onPageChange: (p) => handlePageChange(p - 1),
            onPageSizeChange: (size) => handlePageSizeChange(Number(size)),
            pageSizeOptions: [25, 50, 100],
          }}
          showActions={false}
        />
      </div>

      {/* Deactivation Dialog */}
      {selectedConfig && (
        <DeactivationDialog
          isOpen={isDeactivateOpen}
          onClose={handleDialogClose}
          config={selectedConfig}
          brandId={brandId}
          brandName={brandName}
          onSuccess={handleSuccess}
        />
      )}

      {/* Activation Dialog */}
      {selectedConfig && (
        <ActivationDialog
          isOpen={isActivateOpen}
          onClose={handleDialogClose}
          config={selectedConfig}
          brandId={brandId}
          brandName={brandName}
          onSuccess={handleSuccess}
        />
      )}

      {/* Edit Dialog */}
      {selectedConfig && (
        <EditConfigDialog
          isOpen={isEditOpen}
          onClose={handleDialogClose}
          config={selectedConfig}
          brandId={brandId}
          brandName={brandName}
          hasFiscalCalendar={hasFiscalCalendar}
          onSuccess={handleSuccess}
        />
      )}
    </>
  );
};

export default ConfigurationTable;
